<template>
  <div class="ag-input-number">
    <el-popover
      v-model="popoverVisible"
      popper-class="input-number-pop"
      placement="top-start"
      :content="content"
      trigger="manual"
    >
      <template #reference>
        <el-input
          ref="input"
          :class="['input-number', isError ? 'error-input' : '']"
          :value="displayValue"
          clearable
          v-bind="componentProps"
          @keydown.up.native.prevent="increase"
          @keydown.down.native.prevent="decrease"
          @blur="handleBlur"
          @focus="handleFocus"
          @input="handleInput"
          @change="handleInputChange"
        />
      </template>
    </el-popover>
  </div>
</template>

<script>

import agEditorMixins from './ag-editor-mixins'
import { Format } from '@cndinfo/cube-web-utils'

const TYPE_MAP = {
  PERCENT: 'percent',
  AMOUNT: 'amount'
}

export default {
  name: 'AgInputNumber',
  mixins: [agEditorMixins],
  props: {
  },
  data() {
    return {
      currentValue: 0,
      userInput: null,
      content: undefined,
      popoverVisible: false,
      isError: false,
      isFocus: false
    }
  },
  computed: {
    minDisabled() {
      return this._decrease(this.value, 1) < this.min
    },
    maxDisabled() {
      return this._increase(this.value, 1) > this.max
    },
    min() {
      return typeof this.componentProps.min === 'number' ? this.componentProps.min : Number.MIN_SAFE_INTEGER // -9007199254740991
    },
    max() {
      return typeof this.componentProps.max === 'number' ? this.componentProps.min : Number.MAX_SAFE_INTEGER // 9007199254740991
    },
    componentProps() {
      return Object.assign(
        {},
        {
          placeholder: this.$t('components.pleaseEnter'),
          size: 'mini',
          autoFocus: false
        },
        this.params?.cellEditorComponentProps || {}
      )
    },
    numPrecision() {
      const stepPrecision = this.getPrecision(1)

      const { decimalDigit } = this.componentProps

      if (decimalDigit !== undefined) {
        return decimalDigit
      } else {
        return Math.max(this.getPrecision(this.value), stepPrecision)
      }
    },
    inputNumberDisabled() {
      return this.componentProps.disabled
    },
    displayValue() {
      if (this.userInput !== null) {
        return this.userInput
      }

      let currentValue = this.currentValue

      if (!currentValue) {
        return currentValue
      }

      const { decimalDigit, type, thousandthSign } = this.componentProps

      if (!this.isFocus) {
        if (typeof currentValue === 'number' && type !== TYPE_MAP.PERCENT) {
          if (decimalDigit !== undefined) {
            currentValue = Format.keepDecimalPlaces(
              currentValue,
              decimalDigit
            )
          }
        }
        if (thousandthSign) {
          currentValue = Format.formatThousandthSign(
            currentValue,
            decimalDigit
          )
        }

        if (type === TYPE_MAP.PERCENT) {
          currentValue = `${currentValue}%`
        }

        if (type === TYPE_MAP.AMOUNT) {
          currentValue = Format.formatPrice(
            currentValue,
            decimalDigit
          )
        }
      }
      return currentValue
    }
  },
  watch: {
    agValue: {
      immediate: true,
      handler(value) {
        let newVal = !value ? value : Number(value)
        if (newVal !== undefined) {
          if (isNaN(newVal)) {
            return
          }
        }

        if (newVal >= this.max) {
          newVal = this.max
        }
        if (newVal <= this.min) {
          newVal = this.min
        }

        this.currentValue = this.formatter(newVal)

        this.userInput = null
      }
    }
  },
  mounted() {
    const { decimalDigit } = this.componentProps

    if (typeof decimalDigit === 'number') {
      this.agValue = Format.keepDecimalPlaces(
        this.params.value,
        decimalDigit
      )
    } else {
      this.agValue = this.params.value
    }

    const { autoFocus } = this.componentProps

    if (autoFocus) {
      this.focus()
    }
  },
  methods: {
    triggerChange(newValue, oldValue) {
      const { cellEditorComponentProps, ...restAgParams } = this.params || {}

      const { change } = cellEditorComponentProps || {}

      change?.(newValue, oldValue, { agParams: restAgParams })
    },
    formatter(value) {
      const { type } = this.componentProps

      if (type === TYPE_MAP.PERCENT) {
        return Format.accMul(value || 0, 100)
      }
      return value
    },
    parser(value) {
      const { type, decimalDigit } = this.componentProps
      if (type === TYPE_MAP.PERCENT) {
        this.agValue = Format.accDiv(
          Format.keepDecimalPlaces(
            Format.accMul(Number(value || 0), 100),
            typeof decimalDigit === 'number' ? decimalDigit + 2 : 2
          ),
          10000
        )
      }
      return value
    },
    setAgValue(val) {
      this.agValue = this.parser(val)
    },
    focus() {
      this.$refs.input.focus()
    },
    toPrecision(num, precision) {
      if (precision === undefined) precision = this.numPrecision
      return parseFloat(Math.round(num * Math.pow(10, precision)) / Math.pow(10, precision))
    },
    getPrecision(value) {
      if (value === undefined) return 0
      const valueString = value.toString()
      const dotPosition = valueString.indexOf('.')
      let precision = 0
      if (dotPosition !== -1) {
        precision = valueString.length - dotPosition - 1
      }
      return precision
    },
    _increase(val, step) {
      if (typeof val !== 'number' && val !== undefined) {
        return this.currentValue
      }

      const precisionFactor = Math.pow(10, this.numPrecision)
      return this.toPrecision((precisionFactor * val + precisionFactor * step) / precisionFactor)
    },
    _decrease(val, step) {
      if (typeof val !== 'number' && val !== undefined) return this.currentValue

      const precisionFactor = Math.pow(10, this.numPrecision)

      return this.toPrecision((precisionFactor * val - precisionFactor * step) / precisionFactor)
    },
    increase() {
      if (this.inputNumberDisabled || this.maxDisabled) return
      const value = this.value || 0
      const newVal = this._increase(value, this.step)
      this.setCurrentValue(newVal)
    },
    decrease() {
      if (this.inputNumberDisabled || this.minDisabled) return
      const value = this.value || 0
      const newVal = this._decrease(value, this.step)
      this.setCurrentValue(newVal)
    },
    handleBlur(event) {
      this.isFocus = false
      this.userInput = null
      this.triggerBlur(event)
    },
    handleFocus(event) {
      const { focusSelect } = this.componentProps
      this.isFocus = true
      if (focusSelect) {
        this.$nextTick(() => {
          event.target.select()
        })
      }
      this.triggerFocus(event)
    },
    setCurrentValue(newVal) {
      const oldVal = this.currentValue

      const { decimalDigit } = this.componentProps

      if (typeof newVal === 'number' && decimalDigit !== undefined) {
        newVal = this.toPrecision(newVal, decimalDigit)
      }
      if (newVal >= this.max) newVal = this.max
      if (newVal <= this.min) newVal = this.min
      if (oldVal === newVal) return
      this.userInput = null
      this.triggerChange(newVal, oldVal)
      this.currentValue = newVal
    },
    handleInput(value) {
      this.userInput = value
      this.setAgValue(value)
    },
    handleInputChange(value) {
      const newVal = value === '' ? undefined : Number(value)

      if (!isNaN(newVal) || value === '') {
        this.setCurrentValue(newVal)
      }
      this.setAgValue(newVal)
      this.userInput = null
    },
    select() {
      this.$refs.input.select()
    }
  }
}
</script>
